// OWMap BSL1 — Left control panel (filters, search, smart pick)
const { useState: useStateLeft, useEffect: useEffectLeft, useRef: useRefLeft } = React;

function LeftPanel({ tweaks, setTweak, onSmartPick, cultureFilter, setCultureFilter, genreFilter, setGenreFilter, styleSel, setStyleSel, duration, setDuration }) {
  const [expanded, setExpanded] = useStateLeft({ genre: true, style: true, duration: false, lang: false, culture: true });
  const [searchQuery, setSearchQuery] = useStateLeft('');
  const [searchResults, setSearchResults] = useStateLeft(null); // null = not searching
  const [searchLoading, setSearchLoading] = useStateLeft(false);
  const debounceRef = useRefLeft(null);

  const toggleCulture = (id) => {
    if (cultureFilter.includes(id)) setCultureFilter(cultureFilter.filter(x => x !== id));
    else setCultureFilter([...cultureFilter, id]);
  };
  const toggleGenre = (id) => {
    if (genreFilter.includes(id)) setGenreFilter(genreFilter.filter(x => x !== id));
    else setGenreFilter([...genreFilter, id]);
  };

  const handleSearch = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    clearTimeout(debounceRef.current);

    if (!q.trim()) { setSearchResults(null); setSearchLoading(false); return; }

    setSearchLoading(true);
    debounceRef.current = setTimeout(() => {
      // Local search first (instant)
      const localCoords = (window.ALL_COORDS || COORDS).filter(c =>
        !c.dormant && (
          (c.name && c.name.includes(q)) ||
          (c.region && c.region.includes(q))
        )
      );

      // Also search stories locally
      const localStories = [];
      localCoords.forEach(coord => {
        getStories(coord.id).forEach(s => {
          if (s.title.includes(q) || s.excerpt.includes(q)) {
            localStories.push({ story: s, coord });
          }
        });
      });

      setSearchResults({ coords: localCoords, stories: localStories });
      setSearchLoading(false);

      // Then try API search (updates if available)
      BSL1API.search(q).then(data => {
        if (data && data.coords) setSearchResults(data);
      }).catch(() => {});
    }, 300);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  return (
    <aside className="left-panel">
      <div className="brand-row">
        <div className="brand-mark" aria-hidden="true">
          <svg viewBox="0 0 32 32" width="22" height="22">
            <circle cx="16" cy="16" r="11" fill="none" stroke="#F5C842" strokeWidth="1.2" opacity="0.85"/>
            <circle cx="16" cy="16" r="3.5" fill="#E87A3C"/>
            <ellipse cx="16" cy="16" rx="11" ry="4" fill="none" stroke="#F5C842" strokeWidth="0.6" opacity="0.5"/>
          </svg>
        </div>
        <div className="brand-text">
          <div className="brand-name">OWMap</div>
          <div className="brand-sub">睡前故事 · BSL1</div>
        </div>
      </div>

      <div className="search-wrap">
        <svg className="search-icon" viewBox="0 0 24 24" width="14" height="14"><circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.4"/><path d="M15.5 15.5 L20 20" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
        <input
          className="search-input"
          placeholder="今夜，去哪里听一个故事…"
          value={searchQuery}
          onChange={handleSearch}
        />
        {searchQuery ? (
          <button className="search-clear" onClick={clearSearch} aria-label="清除搜索">×</button>
        ) : (
          <kbd className="kbd">⌘K</kbd>
        )}
      </div>

      {/* Search results dropdown */}
      {searchResults && (
        <div className="search-results">
          {searchLoading && <div className="search-loading">搜索中…</div>}
          {!searchLoading && searchResults.coords.length === 0 && searchResults.stories.length === 0 && (
            <div className="search-empty">未找到相关地点或故事</div>
          )}
          {searchResults.coords.map(c => (
            <button key={c.id} className="search-result-row" onClick={() => { clearSearch(); /* parent handles coord selection via globe */ }}>
              <span className="result-dot" style={{ background: CULTURES[c.culture].color }}></span>
              <span className="result-name">{c.name}</span>
              <span className="result-region">{c.region}</span>
            </button>
          ))}
          {searchResults.stories.slice(0, 4).map(({ story, coord }) => (
            <button key={story.id} className="search-result-row search-result-story">
              <span className="result-icon">♪</span>
              <div className="result-story-text">
                <span className="result-story-title">{story.title}</span>
                <span className="result-story-meta">{coord.name} · {story.duration}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="filter-stack">
        <FilterGroup title="故事类型" expanded={expanded.genre} onToggle={() => setExpanded({...expanded, genre: !expanded.genre})}>
          <div className="chip-row">
            {GENRES.map(g => (
              <button key={g.id} className={`chip ${genreFilter.includes(g.id) ? "chip-on" : ""}`} onClick={() => toggleGenre(g.id)}>{g.label}</button>
            ))}
          </div>
        </FilterGroup>

        <FilterGroup title="受众风格" expanded={expanded.style} onToggle={() => setExpanded({...expanded, style: !expanded.style})}>
          <div className="seg">
            {STYLES.map(s => (
              <button key={s.id} className={`seg-btn ${styleSel === s.id ? "seg-on" : ""}`} onClick={() => setStyleSel(s.id)}>{s.label}</button>
            ))}
          </div>
        </FilterGroup>

        <FilterGroup title="时长 · 分钟" expanded={expanded.duration} onToggle={() => setExpanded({...expanded, duration: !expanded.duration})}>
          <div className="duration-row">
            <span className="dur-num">{duration[0]}</span>
            <div className="dur-track">
              <div className="dur-fill" style={{ left: `${(duration[0]-3)/17*100}%`, right: `${100 - (duration[1]-3)/17*100}%` }}></div>
              <input type="range" min="3" max="20" value={duration[0]} onChange={e => setDuration([Math.min(+e.target.value, duration[1]-1), duration[1]])} className="dur-input"/>
              <input type="range" min="3" max="20" value={duration[1]} onChange={e => setDuration([duration[0], Math.max(+e.target.value, duration[0]+1)])} className="dur-input"/>
            </div>
            <span className="dur-num">{duration[1]}</span>
          </div>
        </FilterGroup>

        <FilterGroup title="文化圈" expanded={expanded.culture} onToggle={() => setExpanded({...expanded, culture: !expanded.culture})}>
          <div className="culture-row">
            {Object.values(CULTURES).map(c => {
              const on = cultureFilter.length === 0 || cultureFilter.includes(c.id);
              return (
                <button key={c.id} className={`culture-pill ${on ? "" : "culture-off"}`} onClick={() => toggleCulture(c.id)} title={c.label}>
                  <span className="culture-dot" style={{ background: c.color, boxShadow: `0 0 10px ${c.color}` }}></span>
                  <span className="culture-label">{c.label}</span>
                </button>
              );
            })}
          </div>
          <button className="culture-clear" onClick={() => setCultureFilter([])}>显示全部</button>
        </FilterGroup>
      </div>

      <div className="smart-pick" onClick={onSmartPick}>
        <div className="smart-glow"></div>
        <div className="smart-content">
          <div className="smart-icon">
            <svg viewBox="0 0 24 24" width="16" height="16"><path d="M12 3 L13.5 9 L20 10 L15 14 L16 20.5 L12 17.5 L8 20.5 L9 14 L4 10 L10.5 9 Z" fill="#F5C842" opacity="0.85"/></svg>
          </div>
          <div className="smart-text">
            <div className="smart-title">为我决定</div>
            <div className="smart-sub">让算法读懂今夜的你</div>
          </div>
          <svg viewBox="0 0 24 24" width="14" height="14" className="smart-arrow"><path d="M6 12 L18 12 M14 8 L18 12 L14 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </div>
      </div>

      <div className="left-foot">
        <button className="foot-btn"><span>语言</span><span className="foot-val">中文 / EN</span></button>
        <button className="foot-btn"><span>会员</span><span className="foot-val">Free</span></button>
      </div>
    </aside>
  );
}

function FilterGroup({ title, expanded, onToggle, children }) {
  return (
    <div className="filter-group">
      <button className="filter-head" onClick={onToggle}>
        <span>{title}</span>
        <svg viewBox="0 0 24 24" width="11" height="11" style={{ transform: expanded ? "rotate(90deg)" : "none", transition: "transform .2s" }}>
          <path d="M9 6 L15 12 L9 18" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {expanded && <div className="filter-body">{children}</div>}
    </div>
  );
}

window.LeftPanel = LeftPanel;
